import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlarmClock, X, HandCoins } from "lucide-react";

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Shows a dismissible banner when:
 * - reminder is enabled in cafe settings
 * - current local hour >= configured hour
 * - cash_at_cafe > threshold
 * - user has NOT dismissed it today (localStorage keyed by date)
 */
export default function CloseReminderBanner({ cashAtCafe }) {
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancel) setSettings(data);
      } catch {
        // silent — banner just won't show
      }
    })();
    const key = `close_reminder_dismissed_${todayKey()}`;
    setDismissed(localStorage.getItem(key) === "1");
    return () => { cancel = true; };
  }, []);

  const dismiss = () => {
    localStorage.setItem(`close_reminder_dismissed_${todayKey()}`, "1");
    setDismissed(true);
  };

  if (!settings || !settings.close_reminder_enabled || dismissed) return null;
  const nowHour = new Date().getHours();
  if (nowHour < settings.close_reminder_hour) return null;
  if ((cashAtCafe || 0) <= (settings.close_reminder_threshold || 0)) return null;

  return (
    <div
      className="mb-6 rounded-2xl border border-terracotta-500/25 bg-terracotta-100 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in-up"
      data-testid="close-reminder-banner"
    >
      <div className="w-11 h-11 rounded-xl bg-terracotta-500 text-white flex items-center justify-center shrink-0">
        <AlarmClock className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-espresso-900 text-base tracking-tight">
          Time to close up?
        </div>
        <div className="text-taupe-700 text-sm mt-0.5">
          You still have <span className="font-semibold text-terracotta-600" data-testid="close-reminder-amount">{formatINR(cashAtCafe)}</span> in the register.
          Consider a handover before locking up.
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/dashboard"
          onClick={(e) => {
            // scroll to Cash-at-Cafe section on the current page
            e.preventDefault();
            const el = document.querySelector('[data-testid="cash-at-cafe-section"]');
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-espresso-800 text-white text-sm font-semibold hover:bg-espresso-900 transition-colors"
          data-testid="close-reminder-cta"
        >
          <HandCoins className="w-4 h-4" /> Handover now
        </Link>
        <button
          onClick={dismiss}
          className="p-2 rounded-lg text-taupe-600 hover:text-espresso-900 hover:bg-white transition-colors"
          data-testid="close-reminder-dismiss"
          aria-label="Dismiss for today"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
